import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Copy, Check, Share2, Gift, Users, Sparkles, Twitter, MessageCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getMyReferralStats } from "@/lib/referrals.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/referrals")({
  head: () => ({
    meta: [
      { title: "Refer & Earn — ForexMind AI" },
      { name: "description", content: "Refer traders and earn free months of ForexMind AI Pro." },
    ],
  }),
  component: ReferralsPage,
});

function ReferralsPage() {
  const fetchStats = useServerFn(getMyReferralStats);
  const { data, isLoading } = useQuery({
    queryKey: ["my-referrals"],
    queryFn: () => fetchStats(),
  });

  const code = data?.referralCode ?? "";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://forexmind.ai";
  const link = code ? `${origin}/?ref=${code}` : "";
  const [copied, setCopied] = useState<"code" | "link" | null>(null);

  const copy = async (val: string, kind: "code" | "link") => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(kind);
      toast.success(kind === "code" ? "Code copied" : "Link copied");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const share = async () => {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({
          title: "ForexMind AI",
          text: "11 AI agents debate every trade setup. Get free access:",
          url: link,
        });
      } catch {
        // user dismissed share sheet
      }
    } else {
      await copy(link, "link");
    }
  };

  const tweetText = encodeURIComponent(
    `I've been using ForexMind AI — 11 specialist AI agents debate every forex setup. Try it free: ${link}`,
  );
  const tweetUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;

  const proUntil = data?.proUntil ? new Date(data.proUntil) : null;
  const monthsEarned = data?.rewardedMonths ?? 0;
  const paidCount = data?.paid ?? 0;
  const pendingCount = data?.pending ?? 0;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-widest text-primary">
        <Sparkles className="h-3 w-3" />
        Refer & earn
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
        Earn 1 free month of Pro for every paid referral.
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Share your code. When someone signs up and upgrades, your Pro access is extended by 30 days.
        No cap.
      </p>

      {/* Earnings hero */}
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <Stat icon={Gift} label="Free months earned" value={`${monthsEarned}`} accent />
        <Stat icon={Users} label="Paid referrals" value={`${paidCount}`} />
        <Stat icon={Users} label="Pending" value={`${pendingCount}`} muted />
      </div>

      {proUntil && proUntil > new Date() && (
        <div className="mt-3 rounded-md border border-buy/30 bg-buy/10 px-3 py-2 text-xs text-buy">
          Your Pro access is active until{" "}
          <span className="font-mono font-semibold">{proUntil.toLocaleDateString()}</span>.
        </div>
      )}

      {/* Share block */}
      <Card className="mt-6 border-border/60 bg-card/80 p-5 backdrop-blur">
        <h2 className="font-display text-lg font-semibold">Your referral code</h2>

        {isLoading ? (
          <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
        ) : !code ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Your code is generating — refresh in a moment.
          </p>
        ) : (
          <>
            <div className="mt-3 flex gap-2">
              <div className="flex flex-1 items-center justify-between rounded-md border border-border/60 bg-input/40 px-3 py-2 font-mono text-lg font-bold tracking-widest text-primary">
                {code}
              </div>
              <Button
                variant="outline"
                onClick={() => copy(code, "code")}
                className="shrink-0"
                aria-label="Copy code"
              >
                {copied === "code" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            <div className="mt-3 space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Or share your link
              </label>
              <div className="flex gap-2">
                <Input value={link} readOnly className="flex-1 font-mono text-xs" />
                <Button
                  variant="outline"
                  onClick={() => copy(link, "link")}
                  className="shrink-0"
                  aria-label="Copy link"
                >
                  {copied === "link" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={share} className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Share2 className="mr-1.5 h-4 w-4" /> Share
              </Button>
              <Button asChild variant="outline">
                <a href={tweetUrl} target="_blank" rel="noopener noreferrer">
                  <Twitter className="mr-1.5 h-4 w-4" /> Post on X
                </a>
              </Button>
              <Button asChild variant="outline">
                <a
                  href={`https://wa.me/?text=${tweetText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
                </a>
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* How it works */}
      <Card className="mt-4 border-border/60 bg-card/60 p-5">
        <h2 className="font-display text-lg font-semibold">How it works</h2>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
          <li>
            <span className="mr-2 font-mono text-primary">1.</span>
            Share your code or link with a trader friend.
          </li>
          <li>
            <span className="mr-2 font-mono text-primary">2.</span>
            They sign up using your link (or paste your code at signup).
          </li>
          <li>
            <span className="mr-2 font-mono text-primary">3.</span>
            When they upgrade to Pro or Elite, you instantly receive{" "}
            <span className="font-semibold text-foreground">+30 days of Pro</span>.
          </li>
        </ol>
        <p className="mt-3 text-[11px] text-muted-foreground">
          No limit. Referrals stack — refer 12 paying users, get a full year of Pro.
        </p>
      </Card>

      {/* Referrals list */}
      {(data?.referrals?.length ?? 0) > 0 && (
        <Card className="mt-4 border-border/60 bg-card/60 p-5">
          <h2 className="font-display text-lg font-semibold">Your referrals</h2>
          <ul className="mt-3 divide-y divide-border/60">
            {data!.referrals.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-muted-foreground">
                  {new Date(r.created_at).toLocaleDateString()}
                </span>
                <StatusBadge status={r.status} />
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  accent,
  muted,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <Card
      className={`flex items-center gap-3 border-border/60 p-4 ${
        accent ? "bg-primary/10 border-primary/40" : "bg-card/60"
      }`}
    >
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-lg ${
          accent ? "bg-primary/20 text-primary" : muted ? "bg-muted/30 text-muted-foreground" : "bg-accent/40 text-foreground"
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="font-mono text-2xl font-bold">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      </div>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    pending: { label: "Signed up", color: "var(--muted-foreground)" },
    paid: { label: "Upgraded", color: "var(--primary)" },
    rewarded: { label: "Rewarded · +30d", color: "var(--buy)" },
  };
  const cfg = map[status] ?? map.pending;
  return (
    <span
      className="rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest"
      style={{
        borderColor: `color-mix(in oklab, ${cfg.color} 45%, transparent)`,
        backgroundColor: `color-mix(in oklab, ${cfg.color} 10%, transparent)`,
        color: cfg.color,
      }}
    >
      {cfg.label}
    </span>
  );
}
